import { useState } from 'react'
import * as XLSX from 'xlsx'
import Toast, { ToastType } from '../../../../utils/toast'
type IndexDataType = {
  title: string[]
  description: string[]
  tableTitle: string[]
  sheetName: string[]
  headerRow: string[]
  dataRow: string[]
  primary: string[]
  primaryLabel: string[]
  secondary: string[]
  secondaryLabel: string[]
}

export type DynamicTableDataInputType = {
  data: String[][]
  file: File
  id: number
  indexData: any
  primaryColumns?: number[]
  secondaryColumns?: number[]
}

export async function ReadExcelFile(excelFile: File) {
  //   const [dynamicTableData, setDynamicTableData] = useState<DynamicTableDataInputType[]>([])
  let dynamicTableData: DynamicTableDataInputType[] = []

  const indexData: any = {
    title: [],
    description: [],
    tableTitle: [],
    sheetName: [],
    headerRow: [],
    dataRow: [],
    primary: [],
    primaryLabel: [],
    secondary: [],
    secondaryLabel: [],
  }

  let primaryColumns: number[] = []
  let secondaryColumns: number[] = []

  function convertExcelDate(excelSerialDate: number): Date {
    const startDate = new Date(1899, 11, 30) // Excel's serial date starts from December 30, 1899
    const date = new Date(startDate.getTime() + excelSerialDate * 86400000) // Multiply by milliseconds in a day
    return date
  }

  const extractSheetData = (workbook: XLSX.WorkBook, sheetNo: number) => {
    const finalExcelData: any[] = []
    workbook.SheetNames.forEach((sheetName, index) => {
      if (index > 0) {
        const sheet = workbook.Sheets[workbook.SheetNames[index]]
        const sheetData: string[][] = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: true,
          defval: '',
        })

        const convertedData = sheetData.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            // Check if it's a number that could be a date
            if (typeof cell === 'number' && cell > 40000 && cell < 60000) {
              // Get the cell reference to check formatting
              const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
              const cellObj = sheet[cellRef]

              // Try multiple approaches to detect dates
              let isDate = false

              if (cellObj) {
                // Method 1: Check cell type
                if (cellObj.t === 'd') {
                  isDate = true
                }

                // Method 2: Check format code for date patterns
                if (
                  cellObj.z &&
                  (/[dmyh]/i.test(cellObj.z) ||
                    cellObj.z.includes('/') ||
                    cellObj.z.includes('-') ||
                    XLSX.SSF.is_date(cellObj.z))
                ) {
                  isDate = true
                }

                // Method 3: Check formatted value
                if (cellObj.w && (cellObj.w.includes('/') || cellObj.w.includes('-'))) {
                  isDate = true
                }
              } else {
                // If no cell object found, assume it's a date (fallback)
                isDate = true
              }

              if (isDate) {
                return convertExcelDate(cell).toLocaleDateString('en-GB')
              }
            }
            return cell
          })
        )

        if (convertedData.length > 1) {
          convertedData[0].forEach((value: string, index) => {
            const lowerValue = value.toLowerCase()

            if (
              indexData.primary?.some(
                (primaryKey: string) => primaryKey.toLowerCase() === lowerValue
              )
            ) {
              primaryColumns.push(index)
            }

            if (
              indexData.secondary?.some((keyValue: string) => keyValue.toLowerCase() === lowerValue)
            ) {
              secondaryColumns.push(index)
            }
          })
        }
        let filterData: string[][] = []
        convertedData.forEach((row, i) => {
          if (indexData.headerRow.length > 0 && i + 1 === indexData.headerRow[0]) {
            filterData.push(row)
          }
          if (indexData.dataRow.length > 0 && i + 1 >= indexData.dataRow[0]) {
            filterData.push(row)
          }
        })
        const filteredData = filterData.filter((row) => row.some((cell) => cell !== ''))
        filteredData.forEach((row, i) => {
          row.forEach((cell, j) => {
            filteredData[i][j] = filteredData[i][j].toString().trim()
          })
        })
        finalExcelData.push(filteredData)
      }
    })
    return finalExcelData
  }
  const parseToIndexDataTypeArray = (data: any[][]): IndexDataType[] => {
    const result: IndexDataType[] = []
    let currentData: IndexDataType | null = null

    data.forEach(([key, ...values]) => {
      switch (key) {
        case 'Table Title':
          if (currentData) {
            result.push(currentData)
          }
          currentData = {
            title: data[0][1] || '',
            description: data[1][1] || '',
            tableTitle: [],
            sheetName: [],
            headerRow: [],
            dataRow: [],
            primary: [],
            primaryLabel: [],
            secondary: [],
            secondaryLabel: [],
          }
          if (currentData) {
            currentData.tableTitle.push(...values)
          }
          break
        case 'SheetName':
          if (currentData) {
            currentData.sheetName.push(...values)
          }
          break
        case 'Header Row':
          if (currentData) {
            currentData.headerRow.push(...values.map(String))
          }
          break
        case 'Data Row':
          if (currentData) {
            currentData.dataRow.push(...values.map(String))
          }
          break
        case 'Primary':
          if (currentData) {
            currentData.primary.push(...values)
          }
          break
        case 'Primary Label':
          if (currentData) {
            currentData.primaryLabel.push(...values)
          }
          break
        case 'Secondary':
          if (currentData) {
            currentData.secondary.push(...values)
          }
          break
        case 'Secondary Label':
          if (currentData) {
            currentData.secondaryLabel.push(...values)
          }
          break
      }
    })

    if (currentData) {
      result.push(currentData)
    }

    return result
  }

  const getIndexData = async (workbook: XLSX.WorkBook) => {
    if (workbook.SheetNames[0].toLowerCase().includes('index')) {
      const indexSheetWorkbook = workbook.Sheets['Index']

      let rows = XLSX.utils.sheet_to_json(indexSheetWorkbook, { header: 1 })
      const filteredData =
        (rows.filter((row: any) => row?.length !== 0) as string[][]) || ([] as string[][])
      filteredData.forEach((row: string[], index) => {
        const updatedRow = row.slice(1)
        indexData[Object.keys(indexData)[index]] = updatedRow
      })
      const myData: IndexDataType[] = parseToIndexDataTypeArray(filteredData)
      return myData
    }
  }

  async function handleChange() {
    if (!excelFile) return
    const allowedFileExtensions = ['xlsx', 'xls', 'xlsm']
    if (excelFile !== undefined) {
      if (allowedFileExtensions.some((extension) => excelFile.name.endsWith(extension))) {
        const formData = new FormData()
        formData.append('excelFile', excelFile)
        await parseExcel()
      } else {
        Toast(`Something went wrong ${allowedFileExtensions.join(', ')}`, ToastType.error)
      }
    }
  }

  const parseAndSaveTable = async (workbook: XLSX.WorkBook) => {
    const indexData = await getIndexData(workbook)
    const excelData: string[][] = extractSheetData(workbook, 2)
    //   setExcelData(excelData)
    dynamicTableData = [
      {
        data: excelData,
        file: excelFile!,
        id: dynamicTableData.length + 1,
        indexData: indexData,
        primaryColumns,
        secondaryColumns,
      },
    ]
  }

  const parseExcel = async () => {
    const data = await excelFile?.arrayBuffer()
    const workbook = XLSX.read(data)
    await parseAndSaveTable(workbook)
  }

  await handleChange()
  return dynamicTableData
}
